import React, {
  memo,
  useState,
  useImperativeHandle,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import cn from 'classnames';
import { useForm, FormProvider, useFieldArray } from 'react-hook-form';
import { useRouter } from 'next/router';
import { Row, Col } from 'components/layout';
import AddSelectOptionEmojiV2 from 'components/AddSelectOptionEmojiV2';
import EventsV2 from 'components/EventsV2';
import { AddTitleValueEmoji } from 'components/TitleValueEmojiV2';
import { Form } from 'components/form';
import MultiSelect from 'components/MultiSelect';
import { ButtonV3 } from 'components/ComponentV2';
import { Gamification } from 'pages/company/Gamification/Gamification.component';
import { StatsAttributes, StatsRequest } from 'lib/models/stats';
import { Option } from 'lib/models/option';
import { SocietyProfileDefaultValuesType } from 'lib/models/student-society';
import { TagAttributes, TagType } from 'lib/models/tag';

import SocietyProfileDetails from '../SocietyProfileDetails';
import { SocietyProfileReturnType } from '../UseSocietyProfile.hook';
import Teammates from '../Teammates';
import SocialLinks from '../SocialLinks';
import SectionWrapper from '../SectionWrapper';
import About from '../About';
import styles from './EditSocietyProfileInfo.module.scss';
import { DragAndDrop } from './CustomLinksDragAndDrop.component';

export interface EditSocietyProfileInfoProps extends SocietyProfileReturnType {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  reference: React.Ref<unknown> | undefined;
  setIshowSaveModal: (value: boolean) => void;
}

export const EditSocietyProfileInfo: React.FC<EditSocietyProfileInfoProps> =
  memo(
    ({
      className = '', // custom class name
      style, // custom style
      universities,
      societyProfile,
      offerings,
      societyOfferings,
      topics,
      primaryTopic,
      societyTopics,
      societyFlagshipEvents,
      societyTeamMembers,
      societyHighlights,
      societyStats,
      onSocietyProfileUpdate,
      addStats,
      removeStats,
      onRemoveBanner,
      societyCustomLinks,
      onUpdateSocietyProfile,
      reference,
      isLoading,
      setIshowSaveModal,
    }: EditSocietyProfileInfoProps) => {
      useImperativeHandle(reference, () => ({
        submitForm() {
          handleSubmit(onSubmit)();
        },
      }));
      const contentClassNames = cn(
        {
          [styles.EditSocietyProfileInfo]: true,
        },
        className
      );

      const {
        name,
        university,
        address,
        instagram_url,
        facebook_url,
        linkedin_url,
        website,
        student_union_url,
        short_description,
        description,
        icon_url,
        banner_url,
      } = societyProfile;

      const router = useRouter();

      const [showSectionProgressBanner, setShowSectionProgressBanner] =
        React.useState<boolean>(false);
      const [
        focusedIncompleteSectionBanner,
        setFocusedIncompleteSectionBanner,
      ] = React.useState<number | undefined>();
      const [showCompleteProgressBanner, setShowCompleteProgressBanner] =
        React.useState(true);

      const nonPrimaryTopics: Option[] | TagAttributes<TagType.Topic>[] =
        useMemo(
          () =>
            (societyTopics as Option[])?.filter(
              (topic) => primaryTopic?.id !== topic.id
            ) ?? [],
          [primaryTopic, societyTopics]
        );

      const topicsValue = useMemo(
        () =>
          [
            primaryTopic && { ...primaryTopic, isPrimary: true },
            ...nonPrimaryTopics,
          ].filter((topic) => !!topic),
        [primaryTopic, nonPrimaryTopics]
      );

      const formData = {
        logo: icon_url,
        banner: banner_url,
        name,
        description,
        short_description,
        university_id: university?.data?.id,
        address,
        instagram_url,
        facebook_url,
        linkedin_url,
        website,
        student_union_url,
        custom_links_attributes: societyCustomLinks,
        topic_ids: topicsValue,
        stats_attributes: societyStats,
        offering_ids: societyOfferings,
        flagship_events_attributes: societyFlagshipEvents,
        managers_attributes: societyTeamMembers,
        student_societies_highlights_attributes: societyHighlights,
      };

      const methods = useForm<SocietyProfileDefaultValuesType>({
        defaultValues: formData,
      });

      const {
        handleSubmit,
        setValue,
        control,
        reset,
        watch,
        formState: { isDirty, isSubmitSuccessful },
      } = methods;

      useEffect(() => {
        if (isSubmitSuccessful) {
          reset(formData);
        }
      }, [isSubmitSuccessful, formData, reset]);

      const {
        fields,
        append,
        remove,
        move,
        update: updateCustomLink,
      } = useFieldArray({
        control,
        name: 'custom_links_attributes',
      });

      const {
        fields: statsFields,
        append: statsAppend,
        remove: statsRemove,
        update: statsUpdate,
      } = useFieldArray({
        control,
        name: 'stats_attributes',
      });

      const {
        fields: offeringFields,
        remove: removeOffering,
        update: updateOffering,
      } = useFieldArray({
        control,
        name: 'offering_ids',
      });

      const {
        fields: topicFields,
        remove: removeTopic,
        update: updateTopic,
      } = useFieldArray({
        control,
        name: 'topic_ids',
      });

      const [selectedOfferings, setSelectedOfferings] = useState<Array<string>>(
        offeringFields.map((offering) => offering.id)
      );

      const [isAboutCompleted, setIsAboutCompleted] = useState<boolean>(false);

      const [selectedTopics, setSelectedTopics] = useState<Array<string>>(
        topicFields.map((topic) => topic.id)
      );

      const {
        fields: highlightFields,
        remove: removeHighlight,
        append: appendHighlight,
        update: updateHighlight,
      } = useFieldArray({
        control,
        name: 'student_societies_highlights_attributes',
      });

      const watchCustomLinkFields = watch('custom_links_attributes');
      const watchStatsFields = watch('stats_attributes');
      const watchHighlightsFields = watch(
        'student_societies_highlights_attributes'
      );
      const watchTeamFields = watch('managers_attributes');

      useEffect(() => {
        setIshowSaveModal(isDirty);
      }, [isDirty]);

      useEffect(() => {
        const { query } = router;
        if (query.id) {
          const index = nonCompletedSection.findIndex(
            (section) => section.id === query.id
          );
          if (index !== -1) {
            handleShowMeButton();
            setFocusedIncompleteSectionBanner(index);
            const element = document.getElementById(
              `section_${nonCompletedSection[index].id}`
            );
            element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }, []);

      const updateCustomLinksPosition = (
        data: SocietyProfileDefaultValuesType
      ) => {
        data?.custom_links_attributes?.forEach((item, index) => {
          item['position'] = index + 1;
        });
        return data;
      };
      const onSubmit = async (data: SocietyProfileDefaultValuesType) => {
        const postData = updateCustomLinksPosition(data);
        await onUpdateSocietyProfile(postData);
      };
      const onRemoveStats = async (value: string) => {
        await removeStats(value);
      };

      const onAddStats = async (data: StatsRequest) => {
        await addStats(data);
      };

      const onDeleteCoverPhoto = async () => {
        await onRemoveBanner(societyProfile?.id);
      };

      const getOfferingFromIds = (ids: Array<string>): Array<Option> => {
        const offeringData = offerings.filter((top) => {
          const isExist = ids?.some((id) => id == top?.value);
          return isExist;
        });

        return offeringData;
      };

      const getTopicsFromIds = (ids: Array<string>): Array<Option> => {
        const topicData = topics.filter((top) => {
          const isExist = ids?.some((id) => id == top?.value);
          return isExist;
        });

        return topicData;
      };

      const onAddOffering = (values: Array<string>) => {
        setSelectedOfferings(values);
        const data = getOfferingFromIds(values);
        setValue('offering_ids', data, { shouldDirty: true });
      };

      const onAddTopics = (values: Array<string>) => {
        const primaryTopic = topicFields.find((topic) => topic.isPrimary);
        setSelectedTopics(values);
        const data = getTopicsFromIds(values).map((topic) => {
          if (topic.value === primaryTopic?.id) {
            return { ...topic, isPrimary: true };
          }
          return topic;
        });
        (data as { isPrimary: boolean }[]).sort(function (x, y) {
          return x.isPrimary === y.isPrimary ? 0 : x ? -1 : 1;
        });
        setValue('topic_ids', data, { shouldDirty: true });
      };

      const customButtonOffering = useCallback(
        ({ onClick }: { onClick: () => void }) => (
          <ButtonV3
            onClick={onClick}
            className={cn(styles.addButton, {
              [styles.smallButton]: offeringFields?.length,
            })}
            startIcon="plus"
            variant="contained"
            color="secondary"
            type="button"
          >
            Add offering
          </ButtonV3>
        ),
        [offeringFields]
      );

      const customButtonTopic = useCallback(
        ({ onClick }: { onClick: () => void }) => (
          <ButtonV3
            onClick={onClick}
            className={cn(styles.addButton, {
              [styles.smallButton]: topicFields?.length,
            })}
            startIcon="plus"
            variant="contained"
            color="secondary"
            type="button"
          >
            Add Topics
          </ButtonV3>
        ),
        [topicFields]
      );
      const bannerImageFiles = watch('banner');
      const profileItem = [
        {
          id: 'cover-photo',
          title: 'Cover photo',
          desc: 'Society pages with photos are more intriguing for students and can increase viewing time up to 30%.',
          isCompleted: !!bannerImageFiles,
        },
        {
          title: 'About',
          desc: 'Use your tagline to hook students so they stop and scroll through the rest of your page. Put more detailed info in the About your society section.',
          id: 'about',
          isCompleted:
            isAboutCompleted || (description && short_description)
              ? true
              : false,
        },
        {
          id: 'links',
          title: 'External Links',
          desc: `Integrate your External Links and Events into one profile page and share them with ease. Events and Links will appear at the top of your profile.
          Events appear automatically once you post them on Huzzle.`,
          isCompleted: watchCustomLinkFields?.length
            ? watchCustomLinkFields?.some((item) =>
                item.link && item.title ? true : false
              )
            : false,
        },
        {
          id: 'social-media-links',
          title: 'Social media links',
          desc: `Add at least 1 social link to spruce up your page. Societies with an active social media presence are more attractive to students and companies.`,
          isCompleted:
            instagram_url ||
            facebook_url ||
            linkedin_url ||
            website ||
            student_union_url
              ? true
              : false,
        },
        {
          id: 'topics',
          title: 'Topics',
          desc: 'Add at least 1 topic and focus. Topics are used to recommend your society to students and companies. The more the merrier, but make sure they are relevant.',
          isCompleted: topicFields?.length ? true : false,
        },
        {
          id: 'stats',
          title: 'Stats',
          desc: "Add any stats or things you'd like to highlight about your society. This can help you stand out and looks cool on your profile page.",
          isCompleted: watchStatsFields?.length
            ? watchStatsFields?.some((item) =>
                item.value && item.title ? true : false
              )
            : false,
        },
        {
          id: 'teams',
          title: 'Team',
          desc: "Add your committee here so students and companies can see who's involved and get traction to your individual profiles.",
          isCompleted: watchTeamFields?.length > 1 ? true : false,
        },
        {
          id: 'offerings',
          title: 'Offerings',
          desc: 'Add the types of events or opportunities you offer so students can see what you do at a glance.',
          isCompleted: offeringFields?.length ? true : false,
        },
        {
          id: 'highlights',
          title: 'Highlights',
          desc: `Add any additional achievements or things you'd like to highlight about your society. This appears at the bottom of your profile, so keep the important highlights in your Stats section.`,
          isCompleted: watchHighlightsFields?.length
            ? watchHighlightsFields?.some((item) => item.name)
            : false,
        },
      ];

      const profileCompleteCount = profileItem.filter(
        (item) => item.isCompleted === true
      ).length;

      const nonCompletedSection = useMemo(() => {
        return profileItem.filter((item) => item.isCompleted === false);
      }, []);

      const profileCompletePercent =
        (profileCompleteCount / profileItem.length) * 100;

      const profileComplete = profileCompletePercent === 100;

      const handleProfileCompletionButton = () => {
        setShowSectionProgressBanner(false);
        setShowCompleteProgressBanner(false);
        setFocusedIncompleteSectionBanner(undefined);
      };

      const handleShowMeButton = () => {
        setShowSectionProgressBanner(true);
        setShowCompleteProgressBanner(false);
        setFocusedIncompleteSectionBanner(0);

        const element = document.getElementById(
          `section_${nonCompletedSection[0]?.id}`
        );
        element?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        window.scrollTo({ top: 112, behavior: 'smooth' });
      };

      const handleNextButton = () => {
        const focusIndex =
          focusedIncompleteSectionBanner === undefined
            ? 0
            : focusedIncompleteSectionBanner + 1;
        setFocusedIncompleteSectionBanner(focusIndex);

        const element = document.getElementById(
          `section_${nonCompletedSection[focusIndex].id}`
        );

        const scrollParent = document.getElementById('EditSocietyProfile');
        const elementPosition = element?.getBoundingClientRect().top;
        const offsetPosition =
          (elementPosition ? elementPosition : 0) +
          (scrollParent?.scrollTop ? scrollParent.scrollTop : 0) -
          200;
        scrollParent?.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      };

      const showNextButton =
        focusedIncompleteSectionBanner !== undefined
          ? nonCompletedSection.length > focusedIncompleteSectionBanner + 1
          : false;

      const isToShowBanner =
        showSectionProgressBanner === true &&
        nonCompletedSection.length > 0 &&
        focusedIncompleteSectionBanner !== undefined;

      const getGamificationProps = (index: number) => ({
        progressDetails: profileItem[index],
        nonCompletedSection: profileItem.length - profileCompleteCount,
        profileCompletePercent: profileCompletePercent,
        showProgressBanner:
          isToShowBanner &&
          focusedIncompleteSectionBanner != undefined &&
          nonCompletedSection[focusedIncompleteSectionBanner]?.id ===
            profileItem[index].id,
        handleNextButton: handleNextButton,
        handleProfileCompletionButton: handleProfileCompletionButton,
        showNextButton: showNextButton,
      });

      const gamificationProps = {
        coverPhoto: getGamificationProps(0),
        about: getGamificationProps(1),
        links: getGamificationProps(2),
        socialLinks: getGamificationProps(3),
        topics: getGamificationProps(4),
        stats: getGamificationProps(5),
        teams: getGamificationProps(6),
        offerings: getGamificationProps(7),
        highlights: getGamificationProps(8),
        flagships: { ...getGamificationProps(5), showProgressBanner: false },
      };

      return (
        <div
          className={contentClassNames}
          style={style}
          data-testid="EditSocietyProfileInfo"
        >
          <FormProvider {...methods}>
            <Form
              autoComplete="off"
              className={styles.EventsNewEvent}
              onSubmit={handleSubmit(onSubmit)}
              id="society-form"
            >
              {showCompleteProgressBanner && (
                <Gamification
                  profileCompletePercent={profileCompletePercent}
                  nonCompletedSection={
                    profileItem.length - profileCompleteCount
                  }
                  handleProfileCompletionButton={handleProfileCompletionButton}
                  showMeButton={!profileComplete}
                  handleShowMeButton={handleShowMeButton}
                  title="Complete your profile"
                  description="Fill out missing information to increase your chances of attracting best candidates!"
                  className={styles.Gamification}
                />
              )}
              <div className={styles.CompanyContent}>
                {societyProfile && (
                  <SocietyProfileDetails
                    {...societyProfile}
                    universities={universities}
                    onUpdate={onSocietyProfileUpdate}
                    societyStats={societyStats}
                    onRemoveStats={onRemoveStats}
                    onAddStats={onAddStats}
                    onDeleteCoverPhoto={onDeleteCoverPhoto}
                    {...gamificationProps.coverPhoto}
                  />
                )}
                <About
                  {...gamificationProps.about}
                  setIsAboutCompleted={setIsAboutCompleted}
                />

                <SectionWrapper
                  title="Links"
                  description="Add few links to your page to help boost their discovery."
                  subDetails={
                    <p className={styles.EditSocietyProfileInfoDesc}>
                      Example: <mark>🎃‍</mark>
                      <mark>Our Halloween page</mark>
                      <mark>https://www.example.com</mark>
                    </p>
                  }
                  addButtonLabel="Add link"
                  onAdd={() => {
                    append({
                      title: '',
                      link: '',
                      emoji: '',
                    });
                  }}
                  contentClassName={styles.dragContent}
                  buttonClassName={styles.dragButton}
                  hasData={fields?.length ? true : false}
                  {...gamificationProps.links}
                >
                  <Row
                    className={cn(styles.sectionRow, styles.sectionRowBlock)}
                  >
                    <DragAndDrop
                      fields={fields}
                      updateCustomLink={updateCustomLink}
                      remove={remove}
                      move={move}
                      name={'custom_links_attributes'}
                    />
                  </Row>
                </SectionWrapper>
                <SocialLinks
                  {...societyProfile}
                  onUpdate={onSocietyProfileUpdate}
                  {...gamificationProps.socialLinks}
                />

                <SectionWrapper
                  title="Topics"
                  description="We use these topics to match students to your events and society, so choose them carefully."
                  contentClassName={styles.contentPadding}
                  {...gamificationProps.topics}
                >
                  <Row rowGap={10}>
                    {topicFields && topicFields?.length
                      ? topicFields.map((topic, index) => (
                          <Col key={topic.id} span={24}>
                            <AddSelectOptionEmojiV2
                              onRemove={() => {
                                const selTopics = selectedTopics.filter(
                                  (top) => top !== topic.id
                                );
                                setSelectedTopics(selTopics);
                                removeTopic(index);
                              }}
                              options={topics.filter(
                                (top) =>
                                  !selectedTopics
                                    .filter(
                                      (selectedTopic) =>
                                        selectedTopic !== topic.id
                                    )
                                    .includes(top.id)
                              )}
                              value={topic.id}
                              title={
                                topic.name || (topic as { label: string }).label
                              }
                              onMakePrimary={() => {
                                const getTopic = topicFields.map(
                                  (topicItem, topicIndex) => {
                                    if (topicIndex === index) {
                                      return {
                                        isPrimary: true,
                                        ...topicItem,
                                      };
                                    } else {
                                      const { isPrimary: _, ...resttopicItem } =
                                        topicItem;
                                      return resttopicItem;
                                    }
                                  }
                                );

                                setValue(
                                  'topic_ids',
                                  getTopic as
                                    | Array<TagAttributes<TagType.Topic>>
                                    | Array<Option & { isPrimary?: boolean }>
                                );
                              }}
                              isPrimary={topic?.isPrimary}
                              primaryLabel="Focus"
                              onChange={(item) => {
                                const [topicItem] = getTopicsFromIds([item]);
                                const selTopics = selectedTopics.filter(
                                  (top) => top !== topic.id
                                );
                                setSelectedTopics([...selTopics, item]);
                                updateTopic(index, topicItem);
                              }}
                            />
                          </Col>
                        ))
                      : null}

                    <Col span={24}>
                      <MultiSelect
                        title="Add Topics"
                        placeholder="Search"
                        options={topics}
                        values={selectedTopics}
                        onChange={onAddTopics}
                        className={styles.multiSelect}
                        dropdownClassName={styles.multiSelectDropdown}
                        showCount={false}
                        disableOptions={false}
                        isSearchable
                        customButton={customButtonTopic}
                      />
                    </Col>
                  </Row>
                </SectionWrapper>
                <SectionWrapper
                  title="Stats"
                  description="Any stats about your society you’d like to share!"
                  addButtonLabel="Add stat"
                  subDetails={
                    <p className={styles.EditSocietyProfileInfoDesc}>
                      Example: <mark>👨‍</mark> <mark>Members</mark>
                      <mark>1.5k</mark>
                    </p>
                  }
                  contentClassName={styles.contentPadding}
                  onAdd={() =>
                    statsAppend({
                      emoji: '',
                      title: '',
                      value: '',
                    })
                  }
                  hasData={statsFields?.length ? true : false}
                  {...gamificationProps.stats}
                >
                  <Row className={styles.sectionRow} rowGap={10}>
                    {statsFields && statsFields?.length
                      ? statsFields.map((stats: StatsAttributes, index) => (
                          <Col key={stats.id} span={24}>
                            <AddTitleValueEmoji
                              index={index}
                              name="stats_attributes"
                              onRemove={() => statsRemove(index)}
                              onUpdate={statsUpdate}
                              isStat={true}
                            />
                          </Col>
                        ))
                      : null}
                  </Row>
                </SectionWrapper>
                <SectionWrapper
                  title="Flagship Events"
                  description="Flagship Events are events that are important to your society, or events you host every year!"
                  {...gamificationProps.flagships}
                >
                  <EventsV2 className={styles.mainPadding}></EventsV2>
                </SectionWrapper>
                <SectionWrapper
                  title="Team"
                  description="Showcase your team here."
                  {...gamificationProps.teams}
                >
                  <Teammates className={styles.mainPadding}></Teammates>
                </SectionWrapper>
                <SectionWrapper
                  title="Offerings"
                  description="What kinds of events or opportunities will you be sharing with students?"
                  subDetails={
                    <p className={styles.EditSocietyProfileInfoDesc}>
                      Example: <mark>Mentoring Programmes</mark>
                      <mark>Networking Events</mark>
                      <mark>Events and Conferences</mark>
                    </p>
                  }
                  contentClassName={styles.contentPadding}
                  {...gamificationProps.offerings}
                >
                  <Row rowGap={16}>
                    {offeringFields && offeringFields?.length
                      ? offeringFields.map((offering, index) => (
                          <Col key={offering.id} span={24}>
                            <AddSelectOptionEmojiV2
                              onRemove={() => {
                                const selOffereings = selectedOfferings.filter(
                                  (offer) => offer !== offering.id
                                );
                                setSelectedOfferings(selOffereings);
                                removeOffering(index);
                              }}
                              options={offerings.filter(
                                (offer) =>
                                  !selectedOfferings
                                    .filter(
                                      (selectedOffer) =>
                                        selectedOffer !== offering.id
                                    )
                                    .includes(offer.id)
                              )}
                              value={offering.id}
                              title={
                                offering.name ||
                                (offering as { label: string }).label
                              }
                              onChange={(item) => {
                                const [offeringItem] = getOfferingFromIds([
                                  item,
                                ]);
                                const selOffereings = selectedOfferings.filter(
                                  (offer) => offer !== offering.id
                                );
                                setSelectedOfferings([...selOffereings, item]);
                                updateOffering(index, offeringItem);
                              }}
                            ></AddSelectOptionEmojiV2>
                          </Col>
                        ))
                      : null}
                    <Col span={24}>
                      <MultiSelect
                        title="Add Topics"
                        placeholder="Search"
                        options={offerings}
                        values={selectedOfferings}
                        onChange={onAddOffering}
                        className={styles.multiSelect}
                        dropdownClassName={styles.multiSelectDropdown}
                        showCount={false}
                        disableOptions={false}
                        isSearchable
                        customButton={customButtonOffering}
                      />
                    </Col>
                  </Row>
                </SectionWrapper>
                <SectionWrapper
                  title="Highlights"
                  description="Add any highlights about your society you'd like to share!"
                  addButtonLabel="Add highlight"
                  subDetails={
                    <p className={styles.EditSocietyProfileInfoDesc}>
                      Example: <mark>Voted Best Society in the UK 🚀</mark>
                    </p>
                  }
                  onAdd={() => {
                    appendHighlight({ name: '', emoji: '' });
                  }}
                  contentClassName={styles.contentPadding}
                  hasData={highlightFields?.length ? true : false}
                  {...gamificationProps.highlights}
                >
                  <Row className={styles.sectionRow} rowGap={16}>
                    {highlightFields && highlightFields?.length
                      ? highlightFields.map((highlight, index) => (
                          <Col
                            span={24}
                            key={highlight?.id}
                            style={{ padding: 0 }}
                          >
                            <AddTitleValueEmoji
                              isHighlight={true}
                              onRemove={() => removeHighlight(index)}
                              index={index}
                              name="student_societies_highlights_attributes"
                              onUpdate={updateHighlight}
                            />
                          </Col>
                        ))
                      : null}
                  </Row>
                </SectionWrapper>
              </div>
              <ButtonV3
                type="submit"
                size="large"
                isLoading={isLoading}
                className={styles.buttons}
              >
                Save changes
              </ButtonV3>
            </Form>
          </FormProvider>
        </div>
      );
    }
  );

EditSocietyProfileInfo.displayName = 'EditSocietyProfileInfo';
